// File: src/pages/Home.js
import React, { useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import MiniChatBot from "../components/MiniChatBot";
import NewsTicker from "../components/NewsTicker";
import config from "../config/config";
import bgJobs from "../assets/backgrounds/expt-1.webp";
import bgQualifikation from "../assets/backgrounds/expt-2.webp";
import bgTools from "../assets/backgrounds/exp-3.webp";
import bgCommunity from "../assets/backgrounds/exp-4.webp";

const sectionData = [
  { id: "jobs", title: "Jobs finden und Chancen nutzen", background: bgJobs },
  { id: "qualifikation", title: "Qualifikationen einfach anerkennen", background: bgQualifikation },
  { id: "tools", title: "Tools fuer deinen Aufstieg", background: bgTools },
  { id: "community", title: "Dein Weg nach Europa", background: bgCommunity },
];

const defaultSettings = {
  enabled: true,
  position: "fixed",
  placement: "top",
  width: "100%",
  background_color: "#facc15",
  text_color: "#000000",
  border: "",
  padding: "10px",
  margin: "0",
  animation_type: "marquee",
  animation_speed: 40,
  pause_on_hover: false,
  autoplay: true,
  show_close_button: true,
  height_px: 42,
};

export default function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const { scrollY } = useScroll();
  const parallax = useTransform(scrollY, [0, 1000], [0, 100]);

  const [tickerSettings, setTickerSettings] = useState(defaultSettings);
  const [tickerMessages, setTickerMessages] = useState([]);

  useEffect(() => {
    document.title = "Experts4Europe | AI Talent Platform";

    (async () => {
      try {
        const rp = await fetch(`${config.API_BASE_URL}/api/newsticker/public`);
        if (rp.ok) {
          const data = await rp.json();
          setTickerSettings({ ...defaultSettings, ...(data?.settings || {}) });
          setTickerMessages(Array.isArray(data?.messages) ? data.messages : []);
          return;
        }
      } catch (_) {}

      try {
        const [s, m] = await Promise.all([
          fetch(`${config.API_BASE_URL}/api/newsticker/settings`),
          fetch(`${config.API_BASE_URL}/api/newsticker/messages`),
        ]);
        const sData = s.ok ? await s.json() : {};
        const mData = m.ok ? await m.json() : [];
        setTickerSettings({ ...defaultSettings, ...sData });
        setTickerMessages(Array.isArray(mData) ? mData : []);
      } catch (e) {
        console.warn("Newsticker: Konnte Daten nicht laden:", e);
      }
    })();
  }, []);

  useEffect(() => {
    document.body.classList.add("landingpage-mode");
    return () => document.body.classList.remove("landingpage-mode");
  }, [location.pathname]);

  return (
    <div className="scroll-smooth snap-y snap-mandatory min-h-screen overflow-y-auto text-slate-100 font-sans">
      <NewsTicker messages={tickerMessages} settings={tickerSettings} />

      <nav className="fixed top-[40px] left-0 right-0 z-[1000] flex flex-wrap items-center justify-between bg-transparent px-4 py-4 shadow-md backdrop-blur-lg sm:px-6 md:px-8">
        <Link to="/" className="text-xl font-extrabold text-slate-100 sm:text-2xl">
          Experts4Europe
        </Link>
        <div className="hidden space-x-4 text-base sm:text-lg md:flex">
          <Link to="/stb/job-marketplace" className="rounded-xl bg-emerald-600/70 px-4 py-2 text-sm font-bold text-white shadow hover:bg-emerald-500/70">Jobs</Link>
          <Link to="/menue/tools" className="rounded-xl bg-emerald-600/70 px-4 py-2 text-sm font-bold text-white shadow hover:bg-emerald-500/70">Tools</Link>
          <Link to="/menue/quali" className="rounded-xl bg-emerald-600/70 px-4 py-2 text-sm font-bold text-white shadow hover:bg-emerald-500/70">Qualifikationen</Link>
          <Link to="/menue/success" className="rounded-xl bg-emerald-600/70 px-4 py-2 text-sm font-bold text-white shadow hover:bg-emerald-500/70">Erfolg</Link>
          <Link to="/menue/support" className="rounded-xl bg-emerald-600/70 px-4 py-2 text-sm font-bold text-white shadow hover:bg-emerald-500/70">Support</Link>
        </div>
        <div className="mt-2 flex gap-2 md:mt-0">
          <button onClick={() => navigate("/login")} className="rounded-xl bg-emerald-600/70 px-6 py-2 text-lg font-bold text-white shadow-xl hover:bg-emerald-500/70">Login</button>
          <button onClick={() => navigate("/register")} className="rounded-xl bg-emerald-600/70 px-6 py-2 text-lg font-bold text-white shadow-xl hover:bg-emerald-500/70">Registrieren</button>
        </div>
      </nav>

      {sectionData.map(({ id, title, background }, index) => (
        <section key={id} id={id} className="relative flex h-screen w-screen snap-start items-center justify-center overflow-hidden">
          <motion.div className="absolute h-full w-full" style={{ y: parallax }}>
            <img src={background} alt={id} className="h-full w-full scale-105 object-cover" />
            <div className="absolute inset-0 bg-black/50" />
          </motion.div>

          <motion.div
            className="relative z-10 px-4 text-center"
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: index * 0.15 }}
          >
            {index === 0 ? (
              <>
                <motion.button
                  onClick={() => navigate("/stb/job-marketplace")}
                  className="w-full rounded-xl bg-emerald-600/70 px-8 py-3 text-lg font-bold text-white shadow-xl hover:bg-emerald-500/70 sm:w-auto"
                  whileHover={{ scale: 1.08 }}
                  animate={{
                    boxShadow: [
                      "0 0 0px rgba(110,231,183,0.3)",
                      "0 0 16px rgba(110,231,183,0.7)",
                      "0 0 0px rgba(110,231,183,0.3)",
                    ],
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  Zur Stellenboerse
                </motion.button>
                <div className="h-6" />
              </>
            ) : null}

            <motion.h2
              className="mb-4 text-3xl font-extrabold tracking-tight drop-shadow-lg sm:text-4xl md:text-5xl lg:text-6xl"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.1 }}
            >
              {title}
            </motion.h2>
            <p className="mx-auto max-w-2xl text-base text-slate-300 drop-shadow-md sm:text-lg md:text-xl">
              Entdecke, wie Experts4Europe Karriere, Qualifikation und neue Chancen in Europa verbindet.
            </p>
          </motion.div>
        </section>
      ))}

      <MiniChatBot />

      <footer className="w-screen bg-slate-900 px-4 py-8 text-center text-slate-400">
        <div>
          <p>&copy; {new Date().getFullYear()} Experts4Europe. Alle Rechte vorbehalten.</p>
          <div className="mt-4 space-x-6">
            <Link to="/impressum" className="hover:text-emerald-300">Impressum</Link>
            <Link to="/agb" className="hover:text-emerald-300">AGB</Link>
            <Link to="/datenschutz" className="hover:text-emerald-300">Datenschutz</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
